import React, { useState, useEffect, useContext, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import axios from 'axios';
import ValidationErrorSummary from './ValidationErrorSummary';
import DonationAmount from './DonationAmount';
import Address from './Address';
import TextInput from './TextInput';
import SelectList from './SelectList';
import ContactNumber from './ContactNumber';
import PropTypes from 'prop-types';
import { DonationAppContext } from './DonationApp';
import FormSection from './FormSection';
import ReCaptcha from './ReCaptcha';

/**
 * Component that returns the donation form.
 * * @params See propTypes.
 * @returns {JSX.Element}
 * @component
 */
const DonationForm = ({ formConfig }) => {
  /**
   * State to manage hiding/revealing text in the Gift Aid section
   */
  const [isShowingFullText, setIsShowingFullText] = useState(false);
  /**
   * The values from DonationAppContext
   */
  const { APP_STATES, setDonationAppState } = useContext(DonationAppContext);

  /**
   * Initialise react hook form.
   * @type {UseFormReturn<FieldValues, object>}
   */
  const formHooks = useForm({
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      AMOUNT: formConfig.DefaultPaymentAmount
    }
  });

  /**
   * Deconstructs the functions from useForm.
   */
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setError
  } = formHooks;

  /**
   * The gift aid amount state value.
   *
   * Used to update gift aid value when the amount is changed.
   */
  const [giftAidAmount, setGiftAidAmount] = useState('0.00');

  /**
   * The address country state object.
   *
   * Used to update the contact number country code when the address country code is updated.
   */
  const [addressCountry, setAddressCountry] = useState({
    numeric: 826,
    alpha2: 'GB'
  });

  /**
   * ReCAPTCHA Ref
   *
   * Used to reset the reCAPTCHA challenge if there was an error.
   * @type {React.MutableRefObject<null>}
   */
  const reCaptchaRef = useRef(null);

  /**
   * useEffect to append Error string to page title if form errors exist.
   */
  useEffect(() => {
    const titleError = 'Error: ';
    if (Object.keys(errors).length !== 0 && !document.title.includes(titleError)) {
      document.title = titleError + document.title;
    }
  });

  /**
   * Formats the telephone number entered to be compliant with global payments.
   *
   * @param {string} number
   * @returns {string}
   */
  const formatPhoneNumber = (number) => {
    const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();

    const parsedNumber = phoneUtil.parseAndKeepRawInput(number);

    const countryCallingCode = parsedNumber.getCountryCode();

    return number.replace('+' + countryCallingCode, countryCallingCode + '|');
  };

  /**
   * The form onSubmit function.
   *
   * Uses the api transactions endpoint to generate the HPP Request Object.
   * @param data
   */
  const onSubmit = (data) => {
    const transactionUrl = `${process.env.REACT_APP_HE_ECOM_API}/Payments/Transactions`;

    data.HPP_CUSTOMER_PHONENUMBER_MOBILE = formatPhoneNumber(data.HPP_CUSTOMER_PHONENUMBER_MOBILE);

    const recaptchaToken = data['g-recaptcha'];
    delete data['g-recaptcha'];

    const apiData = {
      txData: JSON.stringify(data),
      grecaptcha: recaptchaToken
    };

    axios.post(transactionUrl,
      apiData,
      {
        headers: {
          siteId: process.env.REACT_APP_HE_SITE_ID,
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.data.HPPJson)
      .then(HPPJson => {
        setDonationAppState(prevState => {
          return {
            ...prevState,
            state: APP_STATES.DONATION_PRE_PROCESSED,
            donation: JSON.parse(HPPJson)
          };
        });
      })
      .catch(error => {

        /**
         * Error response 400 means there are validation errors not captured on the front end.
         */
        if (error.response.status === 400) {
          const validationResult = error.response.data.ValidationResult;

          if ('g-recaptcha' in validationResult) {
            reCaptchaRef.current.reset();
          }

          Object.entries(validationResult).forEach(([name, errorMessage]) => {
            setError(name, { type: 'manual', message: errorMessage });
          });

          return;
        }

        /**
         * Any other error, show the <ApplicationError /> Component
         */
        setDonationAppState(prevState => {
          return {
            ...prevState,
            state: APP_STATES.ERROR,
            error: (
              <>
                <p>There was a problem processing your donation.</p>
              </>
            )
          };
        });
      });
  };

  /**
   * Calculate the gift aid amount based on current amount.
   */
  const calculateGiftAid = () => {
    const giftAidAmount = (getValues('AMOUNT') * 0.25).toFixed(2);

    setGiftAidAmount(giftAidAmount);
  };

  useEffect(() => {
    calculateGiftAid();
  }, []);

  /**
   * Hide/show Gift Aid text
   */
  const handleReadMoreClick = e => {
    const showText = !isShowingFullText;
    e.preventDefault();
    setIsShowingFullText(showText);
  };

  return (
    <>

      {
        errors && Object.keys(errors).length !== 0 &&
        <ValidationErrorSummary errors={errors}/>
      }

      <FormProvider {...formHooks}>
        <form id="donation-form">

          <FormSection
            heading="I would like to donate"
            fieldset={true}>
            <>
              <SelectList
                name="APPEAL"
                label="Choose the appeal you would like to donate towards"
                validation={formConfig.Fields.APPEAL}
                placeholder="Select an appeal"
                options={formConfig.ProductOptions}
                optionTextKey="Label"
                optionValueKey="Value"/>

              <DonationAmount
                formConfig={formConfig}
                calculateGiftAid={calculateGiftAid}/>
            </>
          </FormSection>

          <FormSection
            heading="Your details"
            bgDark={true}
            fieldset={true}>
            <>
              <TextInput
                label="First name"
                name="HPP_CUSTOMER_FIRSTNAME"
                validation={formConfig.Fields.HPP_CUSTOMER_FIRSTNAME}/>

              <TextInput
                label="Last name"
                name="HPP_CUSTOMER_LASTNAME"
                validation={formConfig.Fields.HPP_CUSTOMER_LASTNAME}/>

              <Address
                formConfig={formConfig}
                addressCountry={addressCountry}
                setAddressCountry={setAddressCountry}/>

              <ContactNumber
                formConfig={formConfig}
                addressCountry={addressCountry}/>

              <TextInput
                label="Email address"
                name="HPP_CUSTOMER_EMAIL"
                validation={formConfig.Fields.HPP_CUSTOMER_EMAIL}
                customValidation={{
                  pattern: {
                    // Source: http://emailregex.com/
                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: formConfig.Fields.HPP_CUSTOMER_EMAIL.ValidationMsg
                  }
                }}/>
            </>
          </FormSection>

          <FormSection
            heading="Gift aid"
            containerClass="mt-md-35"
            fieldset={true}>
            <>
              <p className="h2 mt-8 text-cairn">
                Add <span className="text-blue-anchor">£{giftAidAmount}</span> to your donation for free
              </p>
              <p className="mt-2">
                This means we receive an additional £{giftAidAmount} for your donation from HM Revenue
                and Customs.
              </p>
              <img
                src="/themes/donate-form/src/assets/img/Gift_Aid_UK_Logo.svg"
                className="mt-4"
                alt="Stylised black writing on a white background reading 'gift aid it'"
                width="148"
                height="52"/>
              <div className="mt-8 d-flex">
                <input
                  type="checkbox"
                  className="form-check-input flex-shrink-0 mt-0"
                  id="COMMENT2"
                  value="Giftaid=true"
                  {...register('COMMENT2')} />
                <label
                  htmlFor="COMMENT2"
                  className="ms-4 fw-normal form-check-label">
                  Yes, I want to Gift Aid this donation and any donations I make in the future or have
                  made in the
                  past four years to The Historic England Foundation.
                </label>
              </div>
              <p className="mt-5">
                <small>
                  I am a UK taxpayer and I understand that if I pay less Income and/or Capital Gains
                  Tax than the
                  amount of Gift Aid claimed on all my donations in the relevant tax year, it is my
                  responsibility to
                  pay any difference.&nbsp;
                  <button
                    type="button"
                    onClick={handleReadMoreClick}
                    className="button button--link p-0 border-0 d-inline-block fw-light text-decoration-underline bg-transparent"
                    aria-controls="gift-link-section-2"
                    aria-expanded={isShowingFullText}>
                    <small>{isShowingFullText ? 'Read less' : 'Read more'}</small>
                  </button>
                </small>
              </p>
              <div
                id="gift-link-section-2"
                className={isShowingFullText ? 'mt-5' : 'visually-hidden'}>
                <small>
                  Please notify The Historic England Foundation if you:
                  <ul>
                    <li>
                      - want to cancel this declaration
                    </li>
                    <li>
                      - change your name or home address
                    </li>
                    <li>
                      - no longer pay sufficient tax on your income and/or capital gains
                    </li>
                  </ul>
                </small>
                <p className="mt-5">
                  <small>
                    If you pay Income Tax at the higher or additional rate and want to receive the
                    additional tax
                    relief
                    due to you, you must include all your Gift Aid donations on your Self-Assessment
                    tax return or ask
                    HM Revenue and Customs to adjust your tax code.
                  </small>
                </p>
              </div>
            </>
          </FormSection>

          <FormSection
            heading="Your privacy"
            bgDark={true}>
            <>
              <p className="mt-8">
                Historic England takes data protection very seriously and we will always store your
                personal data
                securely. We need your personal details to process and manage your donation. Your
                information may be
                processed on our behalf by carefully selected suppliers in order to administer payments.
                We will not
                share your information unless required to do so by law. For more information on how we
                look after your
                personal data please visit our <a
                  href="https://historicengland.org.uk/terms/privacy-cookies">privacy
                  policy
                </a>.
              </p>
            </>
          </FormSection>

          <FormSection
            containerClass="mt-md-35"
            heading="Contact preferences">
            <>
              <p className="mt-8">
                Your support helps Historic England deliver exciting and innovative projects across the
                country.
                We&apos;d
                love to keep in touch with you about our appeals and updates on our work championing and
                protecting
                England&apos;s historic environment. We will never share your details with anyone else
                to use for
                their
                              purposes. If you no longer wish to hear from us, please email us at&nbsp;<a className="text-break" href="mailto:info@historicenglandfoundation.org.uk">info@HistoricEnglandFoundation.org.uk</a>&nbsp;
                detailing your name and the communication channel(s) you wish to opt out from.
              </p>
              <fieldset
                id="contact_preferences"
                className="mt-5">
                <legend className="mb-0 fw-normal fs-6 float-none">
                  Please let us know how you would like to hear from us:
                </legend>

                <div className="d-flex flex-column flex-sm-md-row mt-4">
                  <div className="d-flex align-items-center">
                    <input
                      id="communication-preference-email"
                      className="form-check-input mt-0"
                      type="checkbox"
                      value="Email" {...register('contact_preferences')} />
                    <label
                      className="ms-4 fw-normal form-check-label"
                      htmlFor="communication-preference-email">
                      Email
                    </label>
                  </div>

                  <div className="d-flex align-items-center mt-4 mt-sm-md-0 ms-sm-md-8">
                    <input
                      id="communication-preference-post"
                      className="form-check-input mt-0"
                      type="checkbox" {...register('contact_preferences')}
                      value="Post"/>
                    <label
                      className="ms-4 fw-normal form-check-label"
                      htmlFor="communication-preference-post">
                      Post
                    </label>
                  </div>

                  <div className="d-flex align-items-center mt-4 mt-sm-md-0 ms-sm-md-8">
                    <input
                      id="communication-preference-phone"
                      className="form-check-input mt-0"
                      type="checkbox"
                      value="Phone" {...register('contact_preferences')} />
                    <label
                      className="ms-4 fw-normal form-check-label"
                      htmlFor="communication-preference-phone">
                      Phone
                    </label>
                  </div>
                </div>
              </fieldset>
            </>
          </FormSection>

          <input
            type="hidden"
            {...register('AMOUNT')} />

          <FormSection
            bgDark={true}
            heading="ReCAPTCHA">

            <ReCaptcha name="g-recaptcha" reCaptchaRef={reCaptchaRef}/>

          </FormSection>

          <div className="mt-40 mb-20 d-flex justify-content-center">
            <input
              type="button"
              className="btn button button--primary h2"
              id="submit_donation"
              value="Donate"
              {...register('submit')}
              onClick={handleSubmit(onSubmit)}
              data-component="hefDonationFormDonateBtn"/>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

DonationForm.propTypes = {
  /**
   * The form settings returned from the settings api.
   */
  formConfig: PropTypes.object.isRequired
};

export default DonationForm;
